import {
  publicCatalogService,
  salesOrderItemService,
} from '@kss/api';
import { adaptProduct } from '../shop/productAdapter';
import type { useCart } from '@kss/ui';

type CartCtx = ReturnType<typeof useCart>;

export interface ReorderResult {
  added: number;
  skipped: number;
  total: number;
}

/**
 * Re-add every line item from a past order back into the cart.
 *
 * Pulls the line items via `salesOrderItemService.filter({ salesOrderId })`,
 * looks each productId up against `publicCatalogService` (which works
 * without auth so a returning shopper can reorder even if their JWT
 * just expired), adapts the row to the storefront's `ShopProduct`
 * shape, then calls `cart.add()` for each.
 *
 * Products whose row has been deleted or whose lookup fails are
 * skipped silently — the caller is given a `{added, skipped, total}`
 * report so the UI can surface "added 3 of 5 items" when relevant.
 *
 * Auto-opens the cart drawer at the end so the shopper sees what
 * landed in their cart. Caller is responsible for navigating away
 * from the order details / orders page if desired.
 */
export async function reorderFromOrder(
  cart: CartCtx,
  salesOrderId: string,
): Promise<ReorderResult> {
  const items = await salesOrderItemService.filter({ salesOrderId });
  const lines = (items ?? []).filter((l) => !!l.productId);

  let added = 0;
  // Sequential lookups so we don't overwhelm the public catalog with
  // 20+ parallel calls for a large reorder. Public reads are cheap
  // but rate-limited by the Cloudflare layer in front of the API.
  for (const line of lines) {
    const productId = String(line.productId);
    const qty = Math.max(1, Number(line.quantity) || 1);
    try {
      const apiProduct = await publicCatalogService.productById(productId);
      if (!apiProduct?.productId) continue;
      // Empty `categoriesById` is fine — cart only consumes id/title/
      // price/image; the category label is decorative.
      const ui = adaptProduct(apiProduct, {});
      cart.add(ui, qty);
      added += 1;
    } catch {
      // Network / not-found — skip this row and continue.
    }
  }

  return {
    added,
    skipped: lines.length - added,
    total: lines.length,
  };
}
